import React from "react";
import Content from "components/Content";
import { Box, Typography, Divider, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CircleIcon from '@mui/icons-material/Circle';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import "./UserManual.css";

const ImagePlaceholder = ({ text, height = "200px" }) => (
  <Box className="manual-image-box" sx={{ height: height }}>
    <Typography className="manual-image-text">{text}</Typography>
  </Box>
);

function UserManualComponent({ isLoading }) {
  return (
    <Content className="swt-dashboard" isLoaded="true">
      <Box className="manual-wrapper">

        <Box sx={{ mb: 5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography className="manual-header-title">Manual de usuario</Typography>
            <Typography className="manual-header-subtitle">Guía de uso básico</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              borderRadius: "50px", color: "#E81F76", borderColor: "#E81F76", padding: "8px 24px",
              fontWeight: "500", textTransform: "none", border: "2px solid #E81F76",
              ":hover": { backgroundColor: "#E81F76", color: "#fff", border: "2px solid #E81F76" },
            }}
          >
            Descargar PDF
          </Button>
        </Box>
        <Box className="manual-section">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h1">1. Introducción</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography className="manual-p-dark">
                Este manual tiene como objetivo brindar una guía completa sobre el uso
                de la Plataforma de Defensa al Consumidor, tanto para ciudadanos como
                para agentes administrativos. Está diseñado con enfoque en claridad,
                accesibilidad y experiencia de usuario (UX), priorizando el entendimiento
                paso a paso. El sistema cuenta con dos grandes flujos: uno orientado al
                ciudadano, sin necesidad de autenticación, y otro orientado a los
                usuarios administrativos mediante un sistema CRM. A lo largo de este
                documento, se detallan los pasos, funcionalidades y recomendaciones
                de uso de cada flujo.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box className="manual-section">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography className="manual-h2">2. Principios de uso del Sistema</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box className="manual-card">
                    <Typography className="manual-h6">Simplicidad</Typography>
                    <Typography className="manual-p" sx={{ mb: 0 }}>Interfaz limpia con enfoque en tareas principales.</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box className="manual-card">
                    <Typography className="manual-h6">Accesibilidad</Typography>
                    <Typography className="manual-p" sx={{ mb: 0 }}>Contrastes correctos, navegación por teclado, textos alternativos y formularios entendibles.</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box className="manual-card">
                    <Typography className="manual-h6">Jerarquía Visual</Typography>
                    <Typography className="manual-p" sx={{ mb: 0 }}>Información organizada por prioridad.</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box className="manual-card">
                    <Typography className="manual-h6">Inclusión</Typography>
                    <Typography className="manual-p" sx={{ mb: 0 }}>Lenguaje claro, libre de tecnicismos innecesarios y con perspectiva de género.</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box className="manual-section">

          <Box sx={{ mb: 0 }}>
            <Typography className="manual-h2">
              3. Flujo A – Ciudadano <span style={{ fontWeight: "400", fontSize: "24px" }}>(sin login)</span>
            </Typography>
          </Box>

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-p-lg">
                <strong style={{ color: "#00AEC3" }}>3.1 Acceso:</strong> El ciudadano accede a la plataforma desde la web oficial. En la pantalla de inicio encontrará dos botones principales:
              </Typography>
              <Typography className="manual-p-lg">
                1. Hacer un Reclamo Común<br />
                2. Hacer un Reclamo IVE
              </Typography>
              <Typography className="manual-p-sm">
                Cada botón lo redirige a un formulario correspondiente, pensado para ser intuitivo, claro y de pocos pasos.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "640px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/acceso-ciudadano.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">3.2 Reclamo Común</Typography>
              <Typography className="manual-p-lg">
                Al seleccionar esta opción, el ciudadano accede a un formulario dividido en tres pasos:
              </Typography>
              <Typography className="manual-p-lg">
                1. Ingreso de datos personales básicos (Nombre, DNI, Email).<br />
                2. Información del reclamo (Tipo de comercio, descripción del problema).<br />
                3. Carga de archivos opcionales (facturas, fotos, comprobantes).
              </Typography>
              <Typography className="manual-p-sm">
                El formulario es validado en tiempo real para evitar errores y es lo más breve posible.<br />
                Una vez completado, el ciudadano hace clic en 'Enviar Reclamo' y recibe una confirmación en pantalla.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "200px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/reclamo-comun.png") lightgray 50% / cover no-repeat' }}>
              </Box>
              {/* <ImagePlaceholder text="[ IMAGEN: Botones  ]" height="150px" /> */}
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">3.3 Reclamo IVE</Typography>
              <Typography className="manual-p-lg">
                El formulario de reclamo IVE es más reducido y orientado a la agilidad. Solo solicita:
              </Typography>
              <Typography className="manual-p-lg">
                - Tipo de problema<br />
                - Fecha del hecho<br />
                - Correo electrónico
              </Typography>
              <Typography className="manual-p-sm">
                Este flujo está optimizado para minimizar barreras de entrada, considerando la sensibilidad del reclamo.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "200px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/reclamo-ive.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">3.4 Seguimiento</Typography>
              <Typography className="manual-p-lg">
                El ciudadano no necesita crear una cuenta. Recibirá un correo electrónico con el número de reclamo y las actualizaciones de estado.
              </Typography>
              <Typography className="manual-p-lg" sx={{ mb: 0 }}>
                Esta estrategia mejora la accesibilidad y elimina fricciones innecesarias.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "743px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/correo-reclamo.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box className="manual-section">

          <Box sx={{ mb: 6 }}>
            <Typography className="manual-h2">4. Flujo B – CRM Administrativo</Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography className="manual-h5">4.1 Perfiles de Usuario</Typography>
            <Typography className="manual-p">
              - <strong>Administrador:</strong> Acceso total, creación y modificación de usuarios, control general del sistema.<br />
              - <strong>OMIC:</strong> Acceso a gestión de reclamos, sin gestión de usuarios.<br />
              - <strong>Soporte:</strong> Exclusivo para resolución de tickets técnicos.
            </Typography>

            <Typography className="manual-h5">4.2 Inicio de Sesión y Dashboard</Typography>
            <Typography className="manual-p">
              El acceso se realiza mediante usuario y contraseña. Al ingresar, cada perfil visualiza un dashboard adaptado a sus funciones con accesos rápidos a las secciones relevantes.
            </Typography>

            <Typography className="manual-h5">4.3 Mesa de Entrada</Typography>
            <Typography className="manual-p">Sistema de semáforo:</Typography>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}><CircleIcon sx={{ color: "#f44336", fontSize: "18px" }} /><Typography className="manual-p" sx={{ mb: 0 }}>- Rojo: Alta prioridad/urgencia</Typography></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}><CircleIcon sx={{ color: "#ffeb3b", fontSize: "18px" }} /><Typography className="manual-p" sx={{ mb: 0 }}>- Amarillo: Prioridad media</Typography></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircleIcon sx={{ color: "#4caf50", fontSize: "18px" }} /><Typography className="manual-p" sx={{ mb: 0 }}>- Verde: Baja prioridad</Typography></Box>
            </Box>
            <Typography className="manual-p-sm">
              El sistema determina estas prioridades automáticamente en base al tipo de reclamo y el tiempo de espera.<br />
              Esta codificación visual ayuda al personal a identificar rápidamente qué casos requieren atención inmediata.
            </Typography>
          </Box>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.4 Gestión de Reclamos</Typography>
              <Typography className="manual-p">Desde la mesa de entrada, los usuarios pueden:</Typography>
              <Typography className="manual-p">
                - Derivar a agentes por nivel (N1, N2, N3)<br />
                - Reasignar casos<br />
                - Rechazar por inconsistencias<br />
                - Agregar comentarios o notas internas
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "279px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/gestion.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.5 Reclamos Hipervulnerables</Typography>
              <Typography className="manual-p">
                Casos sensibles con acceso separado para garantizar atención prioritaria.<br />
                Estos reclamos pueden incluir violencia, salud, personas mayores o embarazos.<br />
                Se visualizan en una sección destacada del CRM.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "188px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/ive.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.6 Vista Completa del Reclamo</Typography>
              <Typography className="manual-p">
                Incluye todos los datos del ciudadano, archivos adjuntos, historial de movimiento, comentarios internos, y posibilidad de:
              </Typography>
              <Typography className="manual-p">
                - Escalar o cerrar el reclamo<br />
                - Asignar soporte técnico<br />
                - Consultar todas las interacciones previas
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "300px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/vista-reclamo.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.7 Filtros y Búsqueda</Typography>
              <Typography className="manual-p">Sistema de búsqueda avanzada por:</Typography>
              <Typography className="manual-p">
                - Número de reclamo/ticket<br />
                - Nombre del ciudadano<br />
                - Fecha<br />
                - Tipo de problema<br />
                - Estado actual
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "394px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/busqueda.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.8 Reportes y Métricas</Typography>
              <Typography className="manual-p">Incluye dashboards e informes exportables que muestran:</Typography>
              <Typography className="manual-p">
                - Volumen de reclamos por fecha<br />
                - Tiempos promedio de atención<br />
                - Cantidad de derivaciones y tipos de reclamos
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "691px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/reportes.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.9 Notificaciones Internas</Typography>
              <Typography className="manual-p">Sistema interno de notificaciones para usuarios administrativos. Informa sobre:</Typography>
              <Typography className="manual-p">
                - Nuevos reclamos<br />
                - Casos reasignados<br />
                - Tickets respondidos<br />
                - Casos cerrados
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "486px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/notificaciones.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

          <Divider className="manual-divider" />

          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h4">4.10 Soporte Técnico</Typography>
              <Typography className="manual-p">El perfil de soporte accede a una bandeja de tickets técnicos enviados por agentes o admins. Resuelven:</Typography>
              <Typography className="manual-p">
                - Fallas de carga<br />
                - Errores de sistema<br />
                - Problemas técnicos específicos
              </Typography>
              <Typography className="manual-p" sx={{ mb: 0 }}>Su intervención es registrada y trazable.</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box width={{ width: "100%", height: "567px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/soporte-tecnico.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
          </Grid>

        </Box>

        <Box className="manual-section">
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography className="manual-h3">5. Buenas Prácticas<br />de Uso</Typography>
              <Typography className="manual-h3">6. Inclusividad<br />y Accesibilidad</Typography>
              <Box width={{ width: "100%", height: "442px", borderTopLeftRadius: "50px", borderBottomRightRadius: "50px", background: 'url("../../assets/manual/practicas.png") lightgray 50% / cover no-repeat' }}>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>

              <Typography className="manual-h5">5. Buenas Prácticas</Typography>
              <ul className="manual-ul">
                <li className="manual-li">Revisar siempre el nivel de prioridad antes de actuar.</li>
                <li className="manual-li">No duplicar reclamos: utilizar el buscador.</li>
                <li className="manual-li">Registrar comentarios claros, con fecha.</li>
                <li className="manual-li">Usar soporte solo cuando el problema excede la capacidad del agente.</li>
              </ul>

              <Typography className="manual-h5">6. Inclusividad y Accesibilidad</Typography>
              <ul className="manual-ul">
                <li className="manual-li">Contraste de colores apto para personas con baja visión.</li>
                <li className="manual-li">Navegación apta por teclado.</li>
                <li className="manual-li">Formularios sin ambigüedad y lenguaje claro.</li>
                <li className="manual-li">Inclusión de perspectiva de género y personas vulnerables.</li>
              </ul>

            </Grid>
          </Grid>
        </Box>

      </Box>
    </Content>
  );
}

export default UserManualComponent;